import React, { useMemo } from 'react';
import classnames from 'classnames';
import * as mobxReact from 'mobx-react';
import { FormattedMessage, injectIntl, IntlShape } from 'react-intl';
import { v4 as uuid } from 'uuid';
import {ITheadSort, ListLayout} from '../../global/tables/table-list-layout';
import TableLoadingRow from '../../global/tables/table-loading-row';
import './event-tracker-list.scss';
import { EventTrackerVM } from '../event-tracker.viewmodel';
import { Paging } from '../../global/_paging/paging-container';
import { SearchInput } from '../../global/search-input/search-input';
import { hasPermission } from '../../../infrastructure/utils/has-permissions';
import { Permission } from '../../../api/roles.dao';
import { groupListByLocalMergedDate } from '../../../infrastructure/utils/common.utils';
import { IEventTrackerDetailsDTO } from '../../../api/events.dao';
import { eventBusinessLines, eventLinks, eventRegions, eventTypes } from '../../../constants/EventValues';
import ControlledMultiSelectionDropdown from '../../global/controlled-multi-selection-dropdown/controlled-multi-selection-dropdown';
import { SelectLeftColumnIcon } from '../../global/icons/icons';
import { EventTrackerNavigation } from '../../_events/_manage-event/shared/navigation/navigation';
import SavedFilters from './_event-tracker-saved-filters/event-tracker-saved-filters';
import EventTrackerListGroupItem from './_event-tracker-list-group-item/event-tracker-list-group-item';
import { EventTrackerListItem } from './_event-tracker-list-item/event-tracker-list-item';
import DateRangePickerDropdown from '../../global/date-range-picker-dropdown/date-range-picker-dropdown';
import { getFormattedDeliveryMemberRoles } from '../../../constants/entity-role-with-intl';
import { COORDINATORS_TEXT, DATE_TEXT, EMPTY_SEARCH_RESULT, TYPE } from '../../../constants/common-labels.constants';
import { accountsNoItems, accountsNoSearchResult, coordinatorsNoItems, coordinatorsNoSearchResult, deliveryTeamNoItems, deliveryTeamNoSearchResult, eventBusinessLineNoSearchResult, eventLinkNoSearchResult, eventRegionNoSearchResult, eventTypeNoSearchResult } from '../event-tracker.text';

const ID_FOR_EVENT_TRACKER_COLUMN_SELECTOR = uuid();

interface IProps {
    intl: IntlShape;
    vm: EventTrackerVM;
}

const EventTrackerList = injectIntl(mobxReact.observer((props: IProps) => {
    const { vm, intl } = props;
    const {
        eventTrackerList,
        isLoading,
        searchQuery,
        withGrouping,
        withDateColumn,
        withTimeColumn,
        withDateRangeColumn,
        withTotalPresenterCombinationsByDate,
        isPlanningNotesFormShown,
        onPlanningNotesClick,
        fetchDeliveryTeamItems,
        setAssignedDeliveryTeam,
        removeDeliveryItem,
        setDeliveryMemberRole,
        trackCancelDeliveryTeamButtonMixPanel
    } = vm;
    const pagingVM = vm.getPagingVM();
    const viewPlanningNotesAccess = hasPermission(Permission.PLANNING_NOTES);
    const canRenderAddMembersButton = hasPermission(Permission.EDIT_EVENT);

    const eventLinksLabel = () => eventLinks(intl).map(item => ({
        id: item.value,
        label: item.label
    }));

    const eventRegionsLabel = () => eventRegions(intl).map(item => ({
        id: item.value,
        label: item.label
    }));

    const eventBusinessLinesLabel = () => eventBusinessLines(intl).map(item => ({
        id: item.value,
        label: item.label
    }));

    const eventTypesLabel = () => eventTypes(intl).map(item => ({
        id: item.value,
        label: item.label
    }));

    const accountColumnLabel = intl.formatMessage({
        id: `oe.common.account`,
        description: 'Label text',
        defaultMessage: 'Account'
    });

    const regionLabel = intl.formatMessage({
        id: `oe.common.region`,
        description: 'Label text',
        defaultMessage: 'Region',
    });

    const businessLineLabel = intl.formatMessage({
        id: `oe.common.businessLine`,
        description: 'Label text',
        defaultMessage: 'Business Line',
    });

    const typeColumnLabel = intl.formatMessage(TYPE);

    const coordinatorsTeamColumnLabel = intl.formatMessage(COORDINATORS_TEXT);

    const deliveryTeamColumnLabel = intl.formatMessage({
        id: `oe.delivery.team`,
        description: 'Label text',
        defaultMessage: 'Delivery Team'
    });

    const linksColumnLabel = intl.formatMessage({
        id: `oe.common.links`,
        description: 'Label text',
        defaultMessage: 'Links'
    });

    const displayedTimeLabel = intl.formatMessage({
        id: `oe.common.time`,
        description: 'Label text',
        defaultMessage: 'Time',
    });

    const nameLabel = intl.formatMessage({
        id: `oe.common.name`,
        description: 'Label text',
        defaultMessage: 'Name',
    });

    const dateLabel = intl.formatMessage(DATE_TEXT);

    const optionalThead = [
        withTimeColumn && {
            id: 'displayedTime',
            title: displayedTimeLabel,
            width: '105px'
        },
        {
            id: 'account',
            title: (
                <ControlledMultiSelectionDropdown
                    entityName={accountColumnLabel}
                    noItemsPlaceholder={accountsNoItems}
                    onApply={vm.handleChangeAccountsFilter}
                    onFetchItems={vm.fetchAccountsList}
                    withBESearch={true}
                    withDebounce={true}
                    noSearchResultText={accountsNoSearchResult}
                    value={vm.selectedFilterAccounts}
                />
            ),
            width: '7.7%'
        },
        {
            id: 'oeNumber',
            title: 'OE #',
            width: '85px'
        },
        {
            id: 'region',
            title: <ControlledMultiSelectionDropdown
                entityName={regionLabel}
                onApply={vm.handleChangeEventRegionsFilter}
                onFetchItems={eventRegionsLabel}
                withBESearch={true}
                noSearchResultText={eventRegionNoSearchResult}
                value={vm.selectedFilterEventRegions}
            />,
            width: '76px'
        },
        {
            id: 'businessLine',
            title: <ControlledMultiSelectionDropdown
                entityName={businessLineLabel}
                onApply={vm.handleChangeBusinessLinesFilter}
                onFetchItems={eventBusinessLinesLabel}
                withBESearch={true}
                noSearchResultText={eventBusinessLineNoSearchResult}
                value={vm.selectedFilterEventBusinessLines}
            />,
            width: '9%',
        },
        {
            id: 'type',
            title: (
                <ControlledMultiSelectionDropdown
                    entityName={typeColumnLabel}
                    onApply={vm.handleChangeEventTypeFilter}
                    onFetchItems={eventTypesLabel}
                    withBESearch={true}
                    noSearchResultText={eventTypeNoSearchResult}
                    value={vm.selectedFilterEventTypes}
                />
            ),
            width: '8.7%'
        },
        {
            id: 'totalPresenters',
            title: 'Total\nPresenters',
            width: '8.5%',
        },
        withTotalPresenterCombinationsByDate && {
            id: 'totalPresenterCombinationsByDate',
            title: 'Daily\nPresenters',
            width: '8.5%',
        },
        {
            id: 'coordinators',
            title: (
                <ControlledMultiSelectionDropdown
                    entityName={coordinatorsTeamColumnLabel}
                    noItemsPlaceholder={coordinatorsNoItems}
                    noSearchResultText={coordinatorsNoSearchResult}
                    withBESearch={true}
                    withDebounce={true}
                    onFetchItems={vm.fetchCoordinatorsList}
                    onApply={vm.handleChangeCoordinatorsFilter}
                    value={vm.selectedFilterCoordinators}
                />
            ),
            width: '7.9%',
        },
        {
            id: 'deliveryTeam',
            title: (
                <ControlledMultiSelectionDropdown
                    entityName={deliveryTeamColumnLabel}
                    noItemsPlaceholder={deliveryTeamNoItems}
                    onApply={vm.handleChangeDeliveryTeamFilter}
                    onFetchItems={vm.fetchDeliveryTeamList}
                    withBESearch={true}
                    withDebounce={true}
                    noSearchResultText={deliveryTeamNoSearchResult}
                    value={vm.selectedFilterDeliveryTeam}
                />
            ),
            width: '8%'
        },
        {
            id: 'links',
            title: (
                <ControlledMultiSelectionDropdown
                    entityName={linksColumnLabel}
                    onApply={vm.handleChangeEventLinksFilter}
                    onFetchItems={eventLinksLabel}
                    withBESearch={true}
                    noSearchResultText={eventLinkNoSearchResult}
                    value={vm.selectedFilterEventLinks}
                />
            ),
            width: '7.4%'
        },
        viewPlanningNotesAccess && {
            id: 'notes',
            title: 'Notes',
            width: '90px'
        },
    ].filter(Boolean);

    const selectedFilterColumnsIds = vm.selectedFilterColumns.map(el => el.id);

    const isColumnSelected = (id: string) => {
        return selectedFilterColumnsIds.includes(id);
    };

    const thead: ITheadSort<void>[] = [
        withDateColumn && isColumnSelected('dateTime') && {
            title: dateLabel,
            width: '160px'
        },
        {
            id: 'name',
            title: nameLabel,
            width: '24%',
        },
        {
            id: 'dateRange',
            title: withDateRangeColumn && (
                <DateRangePickerDropdown
                    label={dateLabel}
                    minDate={new Date()}
                    onApply={vm.handleChangeDateRangeFilter}
                    value={[vm.selectedFilterStartDate[0]?.id, vm.selectedFilterEndDate[0]?.id]}
                    classNames='date-range-picker'
                />
            ),
        },
        // eslint-disable-next-line no-unused-vars
        ...optionalThead.filter(({ id }) => isColumnSelected(id)).map(({ id, ...rest }) => ({
            ...rest
        })),
        {
            title: (
                <ControlledMultiSelectionDropdown
                    key={ID_FOR_EVENT_TRACKER_COLUMN_SELECTOR}
                    entityName=''
                    onApply={vm.handleChangeEventFilterColumns}
                    withIcon={<SelectLeftColumnIcon />}
                    onFetchItems={() => new Promise(resolve => resolve(vm.defaultColumns))}
                    value={vm.selectedFilterColumns}
                    defaultValue={vm.defaultColumns}
                    withoutHighlight
                    classNames='aligned-right'
                />
            ),
            width: '55px'
        },
        {
            customNode:
                <div className='search-input-container'>
                    <SearchInput
                        placeholder={intl.formatMessage({
                            id: 'oe.common.searchEvents',
                            defaultMessage: 'Search',
                            description: 'Search placeholder'
                        })}
                        resetSearch={vm.resetSearchQuery}
                        onChange={(e, value) => vm.onChangeQuery(value)}
                        withResetSearchButton
                        value={vm.searchQuery}
                    />
                </div>,
            width: '145px'
        }
    ].filter(Boolean);

    if (thead.length < 7) {
        if (withDateRangeColumn) {
            thead[thead.findIndex(el => el?.id === 'dateRange')].width = 'auto';
        } else {
            thead[thead.findIndex(el => el.title === nameLabel)].width = 'auto';
        }
    }

    const isEmpty = !isLoading && !eventTrackerList?.length;
    const meetingGroups = useMemo(() =>
        withGrouping && groupListByLocalMergedDate<IEventTrackerDetailsDTO>(eventTrackerList || []).map(meetings => {
            const [{ mergedDateTime, eventId }] = meetings;
            const filteredMeetings = meetings.reduce((acc, curr) => {
                if (!acc.some(entry => entry.eventId === curr.eventId)) {
                    acc.push(curr);
                }
                return acc;
            }, []);

            return {
                id: `${mergedDateTime}_${eventId}`,
                mergedDateTime: mergedDateTime,
                meetings: filteredMeetings
            };
        }), [eventTrackerList, withGrouping]);

    const deliveryMemberRoles = getFormattedDeliveryMemberRoles(intl);

    return (
        <div>
            <section className='horizontal-row horizontal-row--with-bottom-margin event-tabs-tracker'>
                <EventTrackerNavigation />
                <div className='horizontal-row__align-to-row-end'>
                    <div className='buttons-block'>
                        <SavedFilters
                            selectedFilters={vm.getSelectedFilters()}
                            onClearFilters={vm.clearFilters}
                            onDeleteView={vm.deleteFilterView}
                            fetchItems={vm.fetchFilterViews}
                            filterViews={vm.filterViews}
                            onSelectViewFilters={vm.selectFilters}
                            onSave={vm.saveFilterView}
                            columnsCount={vm.defaultColumns.length}
                        />
                    </div>
                </div>
            </section>
            <section className={classnames('event-tracker-list fixed-list-layout', {
                'event-tracker-list--with-date-column': withDateColumn && isColumnSelected('dateTime')
            })}>
                <div className='event-tracker-layout-wrapper'>
                    <ListLayout
                        sortOptions={{
                            theads: thead
                        }}
                    >
                        {!isLoading && !isEmpty && withGrouping && meetingGroups.map(group => {
                            return <EventTrackerListGroupItem
                                key={group.id}
                                searchQuery={searchQuery}
                                viewPlanningNotesAccess={viewPlanningNotesAccess}
                                canRenderAddMembersButton={canRenderAddMembersButton}
                                colSpan={thead.length}
                                vm={vm}
                                group={group}
                                intl={intl}
                                showColumn={isColumnSelected}
                                withDateColumn={withDateColumn}
                                withTimeColumn={withTimeColumn}
                                withTotalPresenterCombinationsByDate={withTotalPresenterCombinationsByDate}
                                wideTdName={selectedFilterColumnsIds[selectedFilterColumnsIds.length - 1]}
                                trackCancelDeliveryTeamButtonMixPanel={trackCancelDeliveryTeamButtonMixPanel}
                                deliveryMemberRoles={deliveryMemberRoles}
                                className='event-tracker-list-group-item'
                            />;
                        })}
                        {!isLoading && !isEmpty && !withGrouping && eventTrackerList.map(event => {
                            const composedId = `${event.accountId}_${event.eventId}`;

                            return (
                                <EventTrackerListItem
                                    {...event}
                                    isFirstInTimeGroup
                                    withDateColumn={withDateColumn}
                                    withTimeColumn={withTimeColumn}
                                    withTotalPresenterCombinationsByDate={withTotalPresenterCombinationsByDate}
                                    composedId={composedId}
                                    key={composedId}
                                    isSelected={isPlanningNotesFormShown(composedId)}
                                    onPlanningNotesClick={onPlanningNotesClick}
                                    fetchDeliveryTeamItems={fetchDeliveryTeamItems}
                                    setAssignedDeliveryTeam={setAssignedDeliveryTeam}
                                    removeDeliveryItem={removeDeliveryItem}
                                    setDeliveryMemberRole={setDeliveryMemberRole}
                                    viewPlanningNotesAccess={viewPlanningNotesAccess}
                                    canRenderAddMembersButton={canRenderAddMembersButton}
                                    startEditEvent={() => vm.editEvent(event, composedId, intl)}
                                    showColumn={isColumnSelected}
                                    mergedDateTime={event.startDateTime}
                                    searchQuery={searchQuery}
                                    fetchEventsList={vm.fetchEventsList}
                                    wideTdName={selectedFilterColumnsIds[selectedFilterColumnsIds.length - 1]}
                                    trackCancelDeliveryTeamButtonMixPanel={trackCancelDeliveryTeamButtonMixPanel}
                                    deliveryMemberRoles={deliveryMemberRoles}
                                />
                            );
                        })}
                        {isLoading && <TableLoadingRow colSpan={thead.length} />}
                    </ListLayout>
                    {isEmpty && (
                        <div className='empty-list-message'>
                            {searchQuery ?
                                <FormattedMessage {...EMPTY_SEARCH_RESULT} />
                                :
                                <FormattedMessage
                                    description='Event Tracker list empty text'
                                    defaultMessage='There are no events.'
                                    id='oe.eventTracker.emptyListText'
                                />
                            }
                        </div>
                    )}
                </div>
                {!isLoading && !isEmpty && <Paging pagingVM={pagingVM} />}
            </section>
        </div>
    );
}));

export default EventTrackerList;
